// Judge.me does not expose preview_badge on this store (API returns 404).
const PRODUCT_REVIEW_API = 'https://api.judge.me/api/v1/widgets/product_review';
const ALL_REVIEWS_API = 'https://api.judge.me/api/v1/widgets/all_reviews_page';

/**
 * @param {string} value
 * @returns {string}
 */
function escapeHtml(value) {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

/**
 * @param {number} count
 * @returns {string}
 */
function formatReviewCount(count) {
  const formatted = Number(count).toLocaleString('en-US');
  return count === 1 ? `${formatted} review` : `${formatted} reviews`;
}

/**
 * @param {number} rating
 * @returns {string}
 */
function renderStarsMarkup(rating) {
  const score = Math.min(5, Math.max(0, Math.round(Number(rating) || 0)));
  const filled = '★'.repeat(score);
  const empty = '☆'.repeat(5 - score);

  if (!empty) {
    return `<span class="topeak-product-hero__reviews-stars-on" aria-hidden="true">${filled}</span>`;
  }

  return `<span class="topeak-product-hero__reviews-stars-on" aria-hidden="true">${filled}</span><span class="topeak-product-hero__reviews-stars-off" aria-hidden="true">${empty}</span>`;
}

/**
 * @param {number} avgRating
 * @param {number} reviewCount
 * @returns {string}
 */
function renderNativeBadge(avgRating, reviewCount) {
  const starsLabel = `${avgRating.toFixed(1)} out of 5 stars`;
  const countLabel = formatReviewCount(reviewCount);

  return `
    <div class="topeak-product-hero__reviews-badge">
      <p class="topeak-product-hero__reviews-stars" aria-label="${escapeHtml(starsLabel)}">
        ${renderStarsMarkup(avgRating)}
      </p>
      <p class="topeak-product-hero__reviews-count">${escapeHtml(countLabel)}</p>
    </div>
  `;
}

/**
 * @param {unknown} payload
 * @returns {string}
 */
function extractWidgetHtml(payload) {
  if (typeof payload === 'string') return payload;
  if (!payload || typeof payload !== 'object') return '';

  const record = /** @type {Record<string, unknown>} */ (payload);

  for (const key of ['all_reviews', 'widget', 'badge', 'html']) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value;
  }

  for (const value of Object.values(record)) {
    if (typeof value === 'string' && value.includes('jdgm')) return value;
  }

  return '';
}

/**
 * @param {string} html
 * @param {string} productHandle
 * @returns {{ avgRating: number, reviewCount: number } | null}
 */
function getProductSummaryFromAllReviews(html, productHandle) {
  const container = document.createElement('div');
  container.innerHTML = html;
  const ratings = [];

  for (const element of container.querySelectorAll('.jdgm-rev')) {
    const link = element.querySelector('.jdgm-rev__prod-link')?.getAttribute('href') || '';

    if (!link.includes(`/products/${productHandle}`)) continue;

    const rating = Number.parseInt(
      element.querySelector('.jdgm-rev__rating')?.getAttribute('data-score') || '0',
      10
    );

    if (rating > 0) ratings.push(rating);
  }

  if (!ratings.length) return null;

  const avgRating = ratings.reduce((total, value) => total + value, 0) / ratings.length;
  return { avgRating, reviewCount: ratings.length };
}

/**
 * @param {string} html
 * @returns {{ avgRating: number, reviewCount: number } | null}
 */
function getProductSummaryFromProductWidget(html) {
  const container = document.createElement('div');
  container.innerHTML = html;

  const summary = container.querySelector('.jdgm-rev-widg, .jdgm-prev-badge');
  if (!summary) return null;

  const avgRating = Number.parseFloat(summary.getAttribute('data-average-rating') || '0');
  const reviewCount = Number.parseInt(summary.getAttribute('data-number-of-reviews') || '0', 10);

  if (!reviewCount) return null;

  return { avgRating, reviewCount };
}

/**
 * @param {string} apiUrl
 * @param {Record<string, string>} params
 * @param {AbortSignal} signal
 * @returns {Promise<string>}
 */
async function fetchWidgetHtml(apiUrl, params, signal) {
  const url = new URL(apiUrl);

  for (const [key, value] of Object.entries(params)) {
    if (value) url.searchParams.set(key, value);
  }

  const response = await fetch(url.toString(), {
    signal,
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) return '';

  const contentType = response.headers.get('content-type') || '';

  if (contentType.includes('application/json')) {
    return extractWidgetHtml(await response.json());
  }

  return await response.text();
}

class TopeakJudgemePreviewBadge extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    this.#loadBadge();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
  }

  async #loadBadge() {
    const host = this.querySelector('[data-judgeme-badge-host]');
    if (!(host instanceof HTMLElement)) return;

    const shopDomain = this.dataset.shopDomain?.trim();
    const apiToken = this.dataset.apiToken?.trim();
    const productId = this.dataset.productId?.trim();
    const productHandle = this.dataset.productHandle?.trim();

    if (!shopDomain || !apiToken || !productId || !productHandle) return;

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const baseParams = {
      shop_domain: shopDomain,
      api_token: apiToken,
    };

    try {
      const productWidgetHtml = await fetchWidgetHtml(
        PRODUCT_REVIEW_API,
        {
          ...baseParams,
          external_id: productId,
          handle: productHandle,
          per_page: '1',
        },
        signal
      );

      let summary = getProductSummaryFromProductWidget(productWidgetHtml);

      if (!summary) {
        const allReviewsHtml = await fetchWidgetHtml(
          ALL_REVIEWS_API,
          { ...baseParams, per_page: '100', page: '1', review_type: 'product-reviews' },
          signal
        );
        summary = getProductSummaryFromAllReviews(allReviewsHtml, productHandle);
      }

      if (!summary) return;

      host.innerHTML = renderNativeBadge(summary.avgRating, summary.reviewCount);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
    }
  }
}

if (!customElements.get('topeak-judgeme-preview-badge')) {
  customElements.define('topeak-judgeme-preview-badge', TopeakJudgemePreviewBadge);
}
